import React, { Suspense, useMemo } from "react";
import { Switch, useLocation } from "react-router-dom";
import AuthRoute from "../auth_routes/AuthRoute";
import PublicRoute from "../auth_routes/PublicRoute";
import PrivateRoute from "../auth_routes/PrivateRoute";

import { PageLoader } from "../components";

import RedirectTo from "../containers/RedirectTo";
import { SocketProvider } from "@/providers/socket-provider";
import { NewEmailDialog } from "@/containers/Mail/components/new-email-dialog";
import { useNewEmailStore } from "@/containers/Mail/store/new-email-store";

const Page404 = React.lazy(
  () => import("../containers/ErrorPage/Page404/Page404"),
);
const Login = React.lazy(() => import("../containers/Login/Login"));
const AuthCallback = React.lazy(
  () => import("../containers/Login/MonetiseLogin/MonetiseCallback"),
);
const ProjectsRouter = React.lazy(
  () => import("../containers/Projects/Projects.Route"),
);
const DashboardRouter = React.lazy(
  () => import("../containers/Dashboard/Dashboard.Route"),
);
const TaskRouter = React.lazy(() => import("../containers/Tasks/Tasks.Route"));
const NoAccess = React.lazy(() => import("../containers/NoAccess"));
const TermsAndCondition = React.lazy(
  () => import("../containers/Terms/TermsAndCondition"),
);
const PrivacyPolicy = React.lazy(
  () => import("../containers/PrivacyPolicy/PrivacyPolicy"),
);
const Unsubscribe = React.lazy(
  () => import("../containers/Unsubscribe/Unsubscribe"),
);
const PrivacyNotice = React.lazy(
  () => import("../containers/PrivacyNotice/PrivacyNotice"),
);
const PipelinesRouter = React.lazy(
  () => import("../containers/Pipelines/Pipelines.Route"),
);
const SettingsRouter = React.lazy(
  () => import("../containers/Settings/Settings.Route"),
);
const SomethingWentWrong = React.lazy(
  () => import("../containers/ErrorPage/WentWrong"),
);
const PagesRouter = React.lazy(() => import("../containers/Pages/Pages.Route"));
const EmailTemplatesTab = React.lazy(() =>
  import("../containers/Settings/tabs/email-templates").then((m) => ({
    default: m.EmailTemplatesTab,
  })),
);
const SequencesPageInner = React.lazy(() =>
  import("../containers/Sequences").then((m) => ({ default: m.SequencesPage })),
);
const OutreachInner = React.lazy(() =>
  import("../containers/Outreach").then((m) => ({ default: m.Outreach })),
);
const InboxInner = React.lazy(() =>
  import("../containers/Inbox").then((m) => ({ default: m.Inbox })),
);

// Page shell for top-level navbar pages (Templates, Sequences). White
// page background — inner content (tables, canvas) provides its own
// visible boundaries. The Sequence editor's blue dotted canvas keeps
// its explicit bg-darker-background.
const PageShell = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-background h-[calc(100vh-50px)]">
    <div className="w-full h-full p-2 sm:p-3 md:p-4 flex flex-col">
      {children}
    </div>
  </div>
);

const TemplatesPage = () => (
  <PageShell>
    <EmailTemplatesTab />
  </PageShell>
);
const SequencesPage = () => (
  <PageShell>
    <SequencesPageInner />
  </PageShell>
);
const OutreachPage = () => (
  <PageShell>
    <OutreachInner />
  </PageShell>
);
const InboxPage = () => (
  <PageShell>
    <InboxInner />
  </PageShell>
);
const PagesPage = (props: object) => (
  <PageShell>
    <PagesRouter {...props} />
  </PageShell>
);
const NoRoute = React.lazy(() => import("../auth_routes/NoRoute"));
const RouteVaildation = React.lazy(
  () => import("../containers/RouteVaildation/RouteVaildation.jsx"),
);
const InviteJoin = React.lazy(() => import("../containers/Invite/InviteJoin"));
const LandingPage = React.lazy(
  () => import("../containers/Landing/LandingPage"),
);

const Routes = () => {
  const isSignedIn = localStorage.getItem("staySignInAG");
  const location = useLocation();
  const { open, setOpen, setSendingTo, setLeadId } = useNewEmailStore();

  const privateRoutes = [
    "/leads",
    "/home",
    "/tasks",
    "/pipelines",
    "/templates",
    "/sequences",
    "/outreach",
    "/inbox",
    "/NoAccess",
    "/settings",
    "/redirect",
    "/sww",
    "/pages",
  ];

  const isPrivateRoute = useMemo(() => {
    return privateRoutes.some((route) => location.pathname.startsWith(route));
  }, [location.pathname]);

  // Routes that render without the app navbar — the loader should match that
  // so we don't flash the navbar before the page resolves.
  const noNavbarRoute = useMemo(() => {
    return location.pathname.startsWith("/settings");
  }, [location.pathname]);

  return (
    <SocketProvider>
      <NewEmailDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSendingTo(null);
          setLeadId(null);
        }}
      />
      <Suspense
        fallback={
          <PageLoader
            isLoading={true}
            showHeader={isPrivateRoute && !noNavbarRoute}
          />
        }
      >
        <Switch>
          <AuthRoute exact path="/" component={Login} />
          <AuthRoute exact path="/login" component={Login} />
          <PublicRoute path="/auth/callback" component={AuthCallback} />
          <AuthRoute
            exact
            path="/oauth/authorize/:client_app_id"
            component={Login}
          />
          <AuthRoute exact path="/loginUser" component={Login} />

          <PublicRoute exact path="/invite" component={InviteJoin} />
          <PublicRoute exact path="/landing" component={LandingPage} />

          <PrivateRoute path="/leads" component={ProjectsRouter} />
          <PrivateRoute exact path="/home" component={DashboardRouter} />
          <PrivateRoute path="/tasks" component={TaskRouter} />
          <PrivateRoute path="/pipelines" component={PipelinesRouter} />
          <PrivateRoute path="/NoAccess" component={NoAccess} />
          <PrivateRoute path="/settings" component={SettingsRouter} />
          <PrivateRoute path="/redirect" component={RedirectTo} />
          <PrivateRoute path="/sww" component={SomethingWentWrong} />
          <PrivateRoute path="/pages" component={PagesPage} />
          <PrivateRoute exact path="/templates" component={TemplatesPage} />
          <PrivateRoute exact path="/sequences" component={SequencesPage} />
          <PrivateRoute exact path="/outreach" component={OutreachPage} />
          <PrivateRoute exact path="/inbox" component={InboxPage} />

          <PublicRoute
            exact
            path="/termsandconditions"
            component={TermsAndCondition}
          />
          <PublicRoute exact path="/privacypolicy" component={PrivacyPolicy} />
          <PublicRoute exact path="/unsubscribe" component={Unsubscribe} />
          <PublicRoute
            exact
            path="/privacy-notice"
            component={PrivacyNotice}
          />
          <NoRoute
            exact
            path="/validation"
            needLogo={false}
            component={RouteVaildation}
          />

          {isSignedIn ? (
            <PrivateRoute component={Page404} />
          ) : (
            <PublicRoute component={Page404} />
          )}
        </Switch>
      </Suspense>
    </SocketProvider>
  );
};

export default Routes;
